import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { type: String }

  connect() {
    this.element.addEventListener('click', this.handleClick.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('click', this.handleClick.bind(this))
  }

  handleClick(event) {
    event.preventDefault()
    this.showEmailModal()
  }

  showEmailModal() {
    // Create modal HTML
    const modalHTML = `
      <div id="waitlist-modal" class="fixed inset-0 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 cursor-pointer transition-[background-color] duration-150 ease-out" style="background-color: rgba(0,0,0,0)">
        <div id="waitlist-modal-content" class="relative p-6 w-full max-w-md shadow-2xl rounded-2xl bg-[#faf8f5] dark:bg-stone-900 border border-stone-200/80 dark:border-stone-700 transform transition-transform duration-200 ease-out scale-95 cursor-default">
          <div class="mt-3">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold tracking-tight text-stone-900 dark:text-stone-100">
                ${this.typeValue === 'business' ? 'Join Our Beta Program' : 'Join the Waitlist'}
              </h3>
              <button type="button" id="close-modal" class="cursor-pointer text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors p-1 -m-1">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <p class="text-sm text-stone-600 dark:text-stone-400 mb-4">
              ${this.typeValue === 'business'
        ? "Enter your email to get early access and be notified when we're ready to launch."
        : "Enter your email to join our waitlist and be first to know when we launch."}
            </p>
            
            <form id="waitlist-form" class="space-y-4">
              <input 
                type="email" 
                id="email-input"
                placeholder="Enter your email address" 
                class="w-full px-4 py-3 rounded-xl border border-stone-200 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-stone-400 dark:focus:ring-stone-500 focus:border-transparent transition"
                required
              >
              
              <div class="flex gap-3">
                <button 
                  type="submit" 
                  class="flex-1 cursor-pointer bg-stone-900 dark:bg-stone-100 text-[#faf8f5] dark:text-stone-900 px-4 py-3 rounded-full font-medium transition-opacity hover:opacity-90"
                >
                  ${this.typeValue === 'business' ? 'Request Early Access' : 'Join Waitlist'}
                </button>
                <button 
                  type="button" 
                  id="cancel-modal"
                  class="cursor-pointer px-4 py-3 rounded-full border border-stone-200 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
            
            <div id="success-message" class="hidden mt-4 p-3 bg-stone-200 dark:bg-stone-700 border border-stone-300 dark:border-stone-600 text-stone-800 dark:text-stone-200 rounded-xl"></div>
            <div id="error-message" class="hidden mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl"></div>
          </div>
        </div>
      </div>
    `

    // Add modal to DOM
    document.body.insertAdjacentHTML('beforeend', modalHTML)

    // Add event listeners
    const modal = document.getElementById('waitlist-modal')
    const modalContent = document.getElementById('waitlist-modal-content')
    const form = document.getElementById('waitlist-form')
    const closeBtn = document.getElementById('close-modal')
    const cancelBtn = document.getElementById('cancel-modal')
    const emailInput = document.getElementById('email-input')

    // Animate modal in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.style.backgroundColor = 'rgba(0,0,0,0.3)'
        modalContent.classList.remove('scale-95')
        modalContent.classList.add('scale-100')
      })
    })

    // Focus email input after animation
    setTimeout(() => emailInput.focus(), 100)

    // Close modal handlers — fade out only (no scale) to avoid pulse/stutter
    const closeModal = () => {
      modal.style.backgroundColor = 'rgba(0,0,0,0)'
      modalContent.style.opacity = '0'
      modalContent.style.transition = 'opacity 100ms ease-out'

      setTimeout(() => {
        modal.remove()
      }, 120)
    }

    closeBtn.addEventListener('click', closeModal)
    cancelBtn.addEventListener('click', closeModal)
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal()
    })

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault()

      const email = emailInput.value.trim()
      if (!email) return

      // Disable form
      const submitBtn = form.querySelector('button[type="submit"]')
      const originalText = submitBtn.textContent
      submitBtn.disabled = true
      submitBtn.textContent = 'Submitting...'

      try {
        const response = await fetch('/waitlist', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            waitlist: {
              email: email,
              signup_type: this.typeValue
            }
          })
        })

        const data = await response.json()

        if (data.success) {
          this.showSuccess(data.message)
          form.style.display = 'none'
        } else {
          this.showError(data.message)
        }
      } catch (error) {
        this.showError('Something went wrong. Please try again.')
      } finally {
        submitBtn.disabled = false
        submitBtn.textContent = originalText
      }
    })

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal()
    })
  }

  showSuccess(message) {
    const successDiv = document.getElementById('success-message')
    successDiv.textContent = message
    successDiv.classList.remove('hidden')

    setTimeout(() => {
      document.getElementById('waitlist-modal')?.remove()
    }, 3000)
  }

  showError(message) {
    const errorDiv = document.getElementById('error-message')
    errorDiv.textContent = message
    errorDiv.classList.remove('hidden')

    setTimeout(() => {
      errorDiv.classList.add('hidden')
    }, 5000)
  }
} 
